import { Checkbox } from '@mui/material';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { BasicTableColumn, DateCell } from '@work4all/components';

import { ProjectHoursBillingBy } from '@work4all/models/lib/Enums/ProjectHoursBillingBy.enum';

import { useFormContextPlus } from '../../../../../containers/mask-overlays/form-plus/use-form-context-plus';

import { ProjectHourBillingOverlayFormValue } from './types';
import { useProjectHourBillingRelatedRecordsSelectability } from './use-project-hour-billing-related-records-selectability';

type Return = {
  title?: string;
  columns?: BasicTableColumn[];
};

export const useProjectHourBillingRelatedRecordsConfig = (): Return => {
  const { t } = useTranslation();
  const { watch } = useFormContextPlus<ProjectHourBillingOverlayFormValue>();

  const billingBy = watch('settings.billingBy');
  const { selectedRecords, toggleRecordSelectable } =
    useProjectHourBillingRelatedRecordsSelectability();

  return useMemo(() => {
    if (billingBy === ProjectHoursBillingBy.ORDER_CONFIRMATION) {
      const columns: BasicTableColumn[] = [
        {
          Header: '',
          accessor: 'id',
          width: 55,
          Cell: ({
            row: {
              original: { id },
            },
          }) => {
            const numericId = Number(id);

            return (
              <SelectCell
                isSelected={selectedRecords.includes(numericId)}
                onClick={() => toggleRecordSelectable(numericId)}
              />
            );
          },
        },
        {
          Header: t('FIELDS.date'),
          accessor: 'contractDate',
          width: 100,
          Cell: DateCell,
        },
        {
          Header: t('FIELDS.number'),
          accessor: 'contractNumber',
          width: 100,
        },
        {
          Header: t('FIELDS.note'),
          accessor: 'note',
          width: 300,
        },
        {
          Header: t('COMMON.CUSTOMER_one'),
          accessor: 'businessPartnerContactCombined.businessPartner.data.name',
          width: 150,
        },
        {
          Header: t('COMMON.CUSTOMER_NUMBER_SHORT'),
          accessor:
            'businessPartnerContactCombined.businessPartner.data.number',
          width: 100,
        },
      ];

      return {
        columns,
        title: t('COMMON.SELECT_ORDERS'),
      };
    }

    if (billingBy === ProjectHoursBillingBy.CUSTOMER) {
      const columns = [
        {
          Header: '',
          accessor: 'id',
          width: 55,
          Cell: ({
            row: {
              original: { id },
            },
          }) => {
            const numericId = Number(id);

            return (
              <SelectCell
                isSelected={selectedRecords.includes(numericId)}
                onClick={() => toggleRecordSelectable(numericId)}
              />
            );
          },
        },
        {
          Header: t('FIELDS.number'),
          accessor: 'number',
          width: 70,
        },
        {
          Header: t('COMMON.NAME'),
          accessor: 'name',
          width: 270,
        },
      ];

      return {
        columns,
        title: t('COMMON.SELECT_CUSTOMERS'),
      };
    }

    return {};
  }, [billingBy, selectedRecords, t, toggleRecordSelectable]);
};

type SelectCellProps = {
  isSelected: boolean;
  onClick: () => void;
};

const SelectCell = ({ isSelected, onClick }: SelectCellProps) => {
  return <Checkbox checked={isSelected} onClick={onClick} />;
};
